import client from "../client";
import { AccountStatus, AttributeCatalogType, PaginationRequest, Status } from "src/types/commonTypes";
import internalClient from "../internalClient";
import { EMPTY_STRING } from "src/components/constant/constant";
import { buildGraphqlFieldsFromMap, graphqlSearchActivityFields } from "../../utils/graphqlFieldMaps";

export interface TransactionRequest extends PaginationRequest {
  filter?: {
    organisation_id?: string;
    transactionType?: string;
    activityName?: string;
    activityStatus?: string;
    destinationWalletId?: string;
    walletIds?: string[];
    userIds?: string[];
    groupIds?: string[];
    created_at?: {
      min?: string;
      max?: string;
    };
    transaction_date?: {
      min?: string;
      max?: string;
    };
    receipt_available?: boolean;
    xero_account_exist?: boolean;
    xero_bank_transaction_exist?: boolean;
    recurring_transaction_id?: string;
    expense_id?: string;
    reimbursement_id?: string;
    reference_activity_id?: string;
    transaction_user_id?: string;
    accounting_status?: AccountStatus;
    accounting_statuses?: AccountStatus[];
    activity_needs_approval?: boolean;
    activity_needs_verification?: boolean;
    ppan?: string;
    ppans?: string[];
    card_id?: string;
  };
  userFilter?: {
    email?: string;
  };
  walletFilter?: {
    displayId?: string;
  };
  sort?: string;
}

export const getAllTransaction = (
  data: TransactionRequest,
  fieldsObj: Record<string, unknown> = graphqlSearchActivityFields
) => {
  const fields = buildGraphqlFieldsFromMap(fieldsObj);

  return client({
    query: `query SearchActivity($input: SearchInput) { 
      search_activity(input: $input) { 
        ${fields}
      } 
    }`,
    variables: {
      input: {
        from: data.from,
        size: data.size,
        sort: data?.sort,
        filter: {
          activity_filter: {
            organisation_id: data?.filter?.organisation_id,
            transaction_type: data?.filter?.transactionType,
            activity_name: data?.filter?.activityName,
            activity_status: data?.filter?.activityStatus,
            destination_wallet_id: data?.filter?.destinationWalletId,
            user_ids: data?.filter?.userIds,
            transaction_user_id: data?.filter?.transaction_user_id,
            wallet_ids: data?.filter?.walletIds,
            group_ids: data?.filter?.groupIds,
            created_at: data?.filter?.created_at,
            transaction_date: data?.filter?.transaction_date,
            receipt_available: data?.filter?.receipt_available,
            xero_account_exist: data?.filter?.xero_account_exist,
            xero_bank_transaction_exist: data?.filter?.xero_bank_transaction_exist,
            recurring_transaction_id: data?.filter?.recurring_transaction_id,
            expense_id: data?.filter?.expense_id,
            reimbursement_id: data?.filter?.reimbursement_id,
            reference_activity_id: data?.filter?.reference_activity_id,
            accounting_status: data?.filter?.accounting_status,
            accounting_statuses: data?.filter?.accounting_statuses,
            activity_needs_approval: data?.filter?.activity_needs_approval,
            activity_needs_verification: data?.filter?.activity_needs_verification,
            ppan: data?.filter?.ppan,
            ppans: data?.filter?.ppans,
            card_id: data?.filter?.card_id
          },
          wallet_filter: {
            display_id: data?.walletFilter?.displayId
          }
        }
      }
    }
  });
};

export const getAllInternalTransaction = (data: TransactionRequest) => {
  return internalClient({
    query: `query SearchActivity($input: SearchInput) { 
      internal_search_activity(input: $input) { 
        total_pages 
        total_size 
        activity_listing { 
          data 
          activity_id 
          activity_name 
          activity_status 
          process_status 
          end_timestamp 
          start_timestamp
          created_at 
          owner_id
          receipt_available
          approval_number
        } 
      } 
    }`,
    variables: {
      input: {
        from: data.from,
        size: data.size,
        sort: data?.sort,
        filter: {
          activity_filter: {
            transaction_type: data?.filter?.transactionType,
            activity_name: data?.filter?.activityName,
            activity_status: data?.filter?.activityStatus,
            destination_wallet_id: data?.filter?.destinationWalletId,
            user_ids: data?.filter?.userIds,
            wallet_ids: data?.filter?.walletIds,
            created_at: data?.filter?.created_at
          },
          wallet_filter: {
            display_id: data?.walletFilter?.displayId
          }
        }
      }
    }
  });
};

export type TransactionDownloadRequest = {
  filter?: {
    organisation_id?: string;
    wallet_ids?: string[];
    user_ids?: string[];
    group_ids?: string[];
    activity_name?: string;
    activity_status?: string;
    created_at?: {
      min?: string;
      max?: string;
    };
    transaction_user_id?: string;
    receipt_available?: boolean;
    ppan?: string;
    xero_account_exist?: boolean;
    xero_bank_transaction_exist?: boolean;
    accounting_status?: AccountStatus;
    amount?: { min?: string; max?: string };
  };
  timeZone?: string;
  columns?: string[];
  report_name?: string;
  // CPC reporting v1 (expense report family)
  group_by?: "MEMBER" | "CARD";
  receipt_threshold?: number;
};

// CPC reporting v1 query fields that all share DownloadReportInput and return
// only a report_id (async generation, polled via get_report).
export type ExpenseReportField =
  | "download_bank_statement"
  | "download_expense_by_member"
  | "download_missing_receipts"
  | "download_awaiting_action";

export const downloadTransaction = (data: TransactionDownloadRequest) => {
  return client({
    query: `query download_transactions ($input: DownloadReportInput) {
      download_transactions(input: $input) {
        report_id
      }
    }`,
    variables: {
      input: {
        filter: {
          activity_filter: data?.filter
        },
        payload: {
          filter: {
            activity_filter: data?.filter
          },
          from: 0,
          size: 10000
        },
        columns: data?.columns,
        timezone: data?.timeZone,
        report_name: data?.report_name || "transaction"
      }
    }
  });
};

// CPC reporting v1: one helper for the four new activity-based reports. The
// GraphQL field name is passed in; group_by / receipt_threshold are only sent
// when relevant to the chosen report.
export const downloadExpenseReport = (field: ExpenseReportField, data: TransactionDownloadRequest) => {
  return client({
    query: `query ${field} ($input: DownloadReportInput) {
      ${field}(input: $input) {
        report_id
      }
    }`,
    variables: {
      input: {
        filter: {
          activity_filter: data?.filter
        },
        payload: {
          filter: {
            activity_filter: data?.filter
          },
          from: 0,
          size: 10000
        },
        columns: data?.columns,
        timezone: data?.timeZone,
        report_name: data?.report_name,
        ...(data?.group_by ? { group_by: data.group_by } : {}),
        ...(data?.receipt_threshold != null ? { receipt_threshold: data.receipt_threshold } : {})
      }
    }
  });
};

export const downloadInternalTransaction = (data: TransactionDownloadRequest) => {
  return internalClient({
    query: `query download_transactions ($input: DownloadReportInput) {
      internal_download_transactions(input: $input) {
        report_id
      }
    }`,
    variables: {
      input: {
        filter: {
          activity_filter: data?.filter
        },
        payload: {
          filter: {
            activity_filter: data?.filter
          },
          from: 0,
          size: 10000
        },
        timezone: data?.timeZone,
        report_name: data?.report_name || "transaction"
      }
    }
  });
};

// CPC-only: mock a card purchase (no Novatti, no wallet deduction). The card is
// chosen via the search_card dropdown (we pass its ppan); reference number is
// generated server-side in transaction-service mockPurchase.

export const listNote = (transactionId: string) => {
  return client({
    query: `query ListNotes($input: ListNotesInput) { 
      list_notes(input: $input) { 
        note { 
          note_id 
          object_id 
          payload { 
            message 
            subject 
          } 
          updated_at
          created_by
        } 
      } 
    }`,
    variables: {
      input: {
        object_id: transactionId
      }
    }
  });
};

export type CreateNoteRequest = {
  objectId: string;
  objectType?: string;
  activityId?: string;
  note: string;
};

export const createNote = (data: CreateNoteRequest) => {
  return client({
    query: `mutation CreateNote($input: CreateNoteInput) {
      create_note(input: $input) {
        note_id
        payload {
          message
          subject
        }
      }
    }`,
    variables: {
      input: {
        payload: {
          message: data.note,
          subject: EMPTY_STRING
        },
        note_type: "BASIC",
        object_id: data.objectId,
        object_type: data.objectType,
        activity_id: data.activityId
      }
    }
  });
};

export const updateNote = (noteId: string, note: string) => {
  return client({
    query: `mutation UpdateNote($input: UpdateNoteInput) {
      update_note(input: $input) {
        note_id
        payload {
          message
          subject
        }
      }
    }`,
    variables: {
      input: {
        note_id: noteId,
        payload: {
          message: note,
          subject: EMPTY_STRING
        }
      }
    }
  });
};

export const transactionPhoto = (assetType: string, transactionId: string) => {
  return client({
    query: `query ListAssets($input: ListAssetsInput) {
      list_assets(input: $input) {
        next_token
        asset {
          asset_id
          attributes {
            bucket_name
            key
          }
          asset_type
          asset_status
        }
      }
    }`,
    variables: {
      input: {
        asset_type: assetType,
        object_id: transactionId
      }
    }
  });
};

export interface searchAssetRequest extends PaginationRequest {
  date?: {
    min?: string;
    max?: string;
  };
  fileTypes?: string[];
  fileIds?: string[];
}

export const searchAsset = (data: searchAssetRequest) => {
  return internalClient({
    query: `query SearchFile($input: SearchInput) {     
        search_file(input: $input) {        
          total_pages                
          total_size                
          files {                    
            file_id             
            file_type  
            process_status               
            attributes {     
              total_rows        
              key              
              bucket_name               
              date               
              e_tag
              original_filename
            }              
          }       
        }    
      }`,
    variables: {
      input: {
        filter: {
          file_filter: {
            date: data.date,
            file_types: data.fileTypes,
            file_ids: data.fileIds
          }
        },
        size: data.size,
        from: data.from
      }
    }
  });
};

export interface searchAssetContentRequest extends PaginationRequest {
  filterContentFilter: {
    file_ids: string[];
    settlement_status?: string[];
    process_status?: string[];
  };
}

export const searchAssetContent = (data: searchAssetContentRequest) => {
  return internalClient({
    query: `query SearchFileContent($input: SearchInput) {           
        search_file_content(input: $input) {        
          total_pages                
          total_size                
          file_content_list {    
            err_message   
            content_id             
            file_id             
            file_type        
            data    
            process_status 
            link_id
            link_type     
            attributes {                
              row_num  
              novatti_amount
              spendable_amount
              ppan
              approval_code
              settlement_status     
              balance_after_deposit
	            balance_before_deposit 
              wallet_before_deposit
            }              
          }       
        }    
      }`,
    variables: {
      input: {
        filter: {
          file_content_filter: data.filterContentFilter
        },
        size: data.size,
        from: data.from
      }
    }
  });
};

export const deletePhoto = (assetId: string) => {
  return client({
    query: `mutation DeleteAsset($input: DeleteAssetInput) {
      delete_asset(input: $input) {
        asset_id
        object_id
      }
    }`,
    variables: {
      input: {
        asset_id: assetId
      }
    }
  });
};

export interface getActivityRequest {
  activity_id?: string;
  link_id?: string;
  owner_id?: string;
}

export const getActivity = (data: getActivityRequest) => {
  return client({
    query: `query GetActivity($input: GetActivityInput) { 
      get_activity(input: $input) { 
          data 
          activity_id 
          activity_name 
          activity_status 
          process_status 
          end_timestamp 
          start_timestamp
          created_at 
          owner_id
          receipt_available
          approval_number
          attributes {
            verified
            tagged_users
            expense_id
          }
      } 
    }`,
    variables: {
      input: data
    }
  });
};

export const settleTransaction = (id: string) => {
  return internalClient({
    query: `mutation processContent($input: ProcessContentInput) {
      process_content(input: $input) {
        content_id
      }
    }`,
    variables: {
      input: {
        content_id: id,
        type: "SETTLEMENT"
      }
    }
  });
};

export const updateGstAmount = (id: string, price: number) => {
  return client({
    query: `mutation updateGst($input: UpdateActivityPriceComponentInput) {
      update_activity_price_components(input: $input) {
        activity_id
      }
    }`,
    variables: {
      input: {
        activity_id: id,
        price_components: {
          gst: price
        }
      }
    }
  });
};

export interface updateActivityRequest {
  activity_id: string;
  tagged_users: string[];
  verified: boolean;
  expense_id?: string;
}

export const updateActivity = (data: updateActivityRequest) => {
  return client({
    query: `mutation updateActivity($input: UpdateActivityInput) {
      update_activity(input: $input) {
        activity_id
      }
    }`,
    variables: {
      input: data
    }
  });
};

export const blockMerchant = (data: { wallet_ids: string[]; merchant_id: string }) => {
  return client({
    query: `mutation block_merchant($input: BlockMerchantInput) {
        block_merchant(input: $input) {
          preference_id
          is_cash_withdrawal_allowed
          spend_limit
          maximum_allowed_limit
          spend_frequency
          group_pay_id
          can_unload_group_pay
          blocked_merchant_ids
        }
      }`,
    variables: { input: data }
  });
};

export const getMerchant = (merchantIds: string[]) => {
  return client({
    query: `query GetMerchant($input: GetMerchantsInput) {
      get_merchants(input: $input) {
        merchant_id
        name
        mcc
        mcc_category
        external_id
      }
    }`,
    variables: { input: { external_ids: merchantIds } }
  });
};

export interface MccRequest extends PaginationRequest {
  filter?: {
    mcc_filter?: {
      mcc_categories?: string[];
      mcc?: string[];
    };
  };
}

export const searchMCC = (data: MccRequest) => {
  return client({
    query: `
    query SearchMCC($input: SearchInput) {
      search_mcc(input: $input) {
      total_pages 
      total_size 
        items {
          mcc_id
          title
          mcc
          mcc_category
        }
      }
    }`,
    variables: {
      input: {
        from: data?.from,
        size: data?.size,
        filter: data.filter
      }
    }
  });
};

export interface SaveActivityPreferenceRequest {
  activity_id: string;
  participated_users: {
    user_id: string;
    amount: number;
  }[];
}

export const saveActivityPreference = (data: SaveActivityPreferenceRequest) => {
  return client({
    query: `mutation SaveActivityPreference($input: ActivityPreferencesInput) {
      save_activity_preferences(input: $input) {
        activity_id
       }
    }`,
    variables: { input: data }
  });
};

export const getActivityPreferences = (activity_id: string) => {
  return client({
    query: `query GetActivityPreferences($input: GetActivityInput) {
      get_activity_preference(input: $input) {
        activity_id
        participated_users {
          user_id
          amount
        }
      }
    }`,
    variables: { input: { activity_id } }
  });
};

export type TransactionAllocation = {
  transaction_allocation_id: string;
  activity_id: string;
  user_id: string;
  status: string;
  allocated_amount: number;
  currency: string;
  gst_type: string;
  gst_rate: number;
  gst_amount: number;
  gl_code: string;
  category: string[];
  // F6: split-transaction counts, populated when the coded GL is a split account.
  no_of_employees?: number;
  no_of_clients?: number;
  integration?: {
    xero?: {
      gl_code?: string;
      tax_type?: string;
      category?: {
        xero_category_id?: string;
        xero_option_id?: string;
      }[];
    };
  };
  user: {
    user_id: string;
    email: string;
    attributes: {
      first_name: string;
      last_name: string;
    };
  };
};

export const getTransactionAllocations = (activity_id: string) => {
  return client({
    query: `query GetTransactionAllocations($input: GetTransactionAllocationsInput) {
      get_transaction_allocations(input: $input) {
        transaction_allocation_id
        activity_id
        user_id
        status
        allocated_amount
        currency
        gst_type
        gst_rate
        gst_amount
        gl_code
        category
        no_of_employees
        no_of_clients
        user {
          user_id
          email
          attributes {
            first_name
            last_name
          }
        }
        integration {
          xero {
            gl_code
            tax_type
            category {
              xero_category_id
              xero_option_id
            }
          }
        }
      }
    }`,
    variables: { input: { activity_id } }
  });
};

export interface CreateTransactionAllocationRequest {
  activity_id: string;
  user_id: string;
  allocated_amount: number;
  currency: string;
  gst_type: string;
  gst_rate?: number;
  gst_amount?: number;
  gl_code?: string;
  category: string[];
  // F6: required (may be 0) when the coded GL is flagged split_transactions.
  no_of_employees?: number;
  no_of_clients?: number;
  integration?: {
    xero?: {
      gl_code?: string;
      tax_type?: string;
      category?: {
        xero_category_id?: string;
        xero_option_id?: string;
      }[];
    };
  };
}
export const createTransactionAllocation = (data: CreateTransactionAllocationRequest) => {
  return client({
    query: `mutation CreateTransactionAllocation($input: CreateTransactionAllocationInput) {
      create_transaction_allocation(input: $input) {
        transaction_allocation_id
        activity_id
        user_id
        status
        allocated_amount
        currency
        gst_type
        gst_rate
        gst_amount
        gl_code
        category
        no_of_employees
        no_of_clients
      }
    }`,
    variables: { input: data }
  });
};

export interface UpdateTransactionAllocationRequest {
  transaction_allocation_id: string;
  activity_id: string;
  user_id: string;
  allocated_amount: number;
  currency: string;
  gst_type: string;
  gst_rate?: number;
  gst_amount?: number;
  gl_code?: string;
  category: string[];
  status: "ACTIVE" | "INACTIVE";
  // F6: required (may be 0) when the coded GL is flagged split_transactions.
  no_of_employees?: number;
  no_of_clients?: number;
  integration?: {
    xero?: {
      gl_code?: string;
      tax_type?: string;
      category?: {
        xero_category_id?: string;
        xero_option_id?: string;
      }[];
    };
  };
}

export type CreateTransactionAllocationRowInput = Omit<CreateTransactionAllocationRequest, "activity_id">;

export const updateTransactionAllocation = (data: UpdateTransactionAllocationRequest) => {
  return client({
    query: `mutation UpdateTransactionAllocation($input: UpdateTransactionAllocationInput) {
      update_transaction_allocation(input: $input) {
        transaction_allocation_id
      }
    }`,
    variables: { input: data }
  });
};

export const createAccountingTransaction = (activity_id: string) => {
  return client({
    query: `mutation CreateAccountingTransaction($input: CreateAccountingTransactionInput) {
      create_accounting_transaction(input: $input) {
        accounting_transaction_id
        
      }
    }`,
    variables: { input: { activity_id } }
  });
};

export const getAccountTransaction = (activity_id: string) => {
  return client({
    query: `query GetAccountTransaction($input: GetAccountingTransactionInput) {
      get_accounting_transaction(input: $input) {
        accounting_transaction_id
        activity_id
        status
        tax_details {
          gst_amount
        }
        xero_bankTransaction {
          synced_at
        }
      }
    }`,
    variables: { input: { activity_id } }
  });
};

export interface UpdateAccountTransactionRequest {
  accounting_transaction_id: string;
  activity_id: string;
  status?: AccountStatus;
  reason?: string;
  tax_details?: {
    gst_amount: number;
  };
}

export const updateAccountTransaction = (data: UpdateAccountTransactionRequest) => {
  return client({
    query: `mutation UpdateAccountTransaction($input: UpdateAccountingTransactionInput) {
      update_accounting_transaction(input: $input) {
        accounting_transaction_id
      }
    }`,
    variables: { input: data }
  });
};

export interface CreateAttributeCatalogRequest {
  code: string;
  type: AttributeCatalogType;
  title: string;
  split_transactions?: boolean;
  display_name?: string;
  hidden?: boolean;
}

export const createAttributeCatalog = (data: CreateAttributeCatalogRequest) => {
  return client({
    query: `mutation CreateAttributeCatalog($input: CreateAttributeCatalogInput) {
      create_attribute_catalog(input: $input) {
        code
        type
        title
        split_transactions
        display_name
        hidden
      }
    }`,
    variables: { input: data }
  });
};

export interface UpdateAttributeCatalogRequest {
  attribute_catalog_id: string;
  code: string;
  type: AttributeCatalogType;
  title: string;
  status: Status;
  split_transactions?: boolean;
  display_name?: string;
  hidden?: boolean;
}

export const updateAttributeCatalog = (data: UpdateAttributeCatalogRequest) => {
  return client({
    query: `mutation UpdateAttributeCatalog($input: UpdateAttributeCatalogInput) {
      update_attribute_catalog(input: $input) {
        attribute_catalog_id
        code
        type
        title
        status
        split_transactions
        display_name
        hidden
      }
    }`,
    variables: { input: data }
  });
};

export interface GetAttributeCatalogRequest extends PaginationRequest {
  attribute_catalog_filter: {
    code?: string;
    codes?: string[];
    type?: AttributeCatalogType;
    attribute_catalog_id?: string;
    attribute_catalog_ids?: string[];
    title?: string;
  };
}

export const getAttributeCatalog = (data: GetAttributeCatalogRequest) => {
  return client({
    query: `query GetAttributeCatalog($input: SearchInput) {
      search_attribute_catalog(input: $input) {
        total_pages
        total_size
        items {
          attribute_catalog_id
          code
          type
          title
          status
          split_transactions
          display_name
          hidden
          organisation_id
          user_id
          wallet_id
        }
      }
    }`,
    variables: {
      input: {
        from: data.from,
        size: data.size,
        filter: {
          attribute_catalog_filter: data.attribute_catalog_filter
        }
      }
    }
  });
};

export const getAccountingAudit = (accounting_id: string) => {
  return client({
    query: `query GetAccountingAudit($input: GetAccountingAuditInput) {
      get_accounting_audit(input: $input) {
        event
      }
    }`,
    variables: { input: { accounting_id } }
  });
};

export type RefreshTransactionsResponse = {
  data?: { refresh_transactions?: { cards_dispatched?: number; scope?: string } };
};

/**
 * Ask the backend to pull any new card transactions from the provider for the
 * signed-in user's scope — their own cards, or the whole organisation for an
 * org-wide role. Takes no arguments by design: scope comes from the token.
 * Fire-and-forget: rows land seconds later, so callers re-read after a pause.
 */
export const refreshTransactions = (): Promise<RefreshTransactionsResponse> => {
  return client(
    {
      query: `mutation RefreshTransactions {
        refresh_transactions {
          cards_dispatched
          scope
        }
      }`,
      variables: {}
    },
    // Background call on login: never surface a toast or bounce the session.
    { suppressErrorToast: true, suppressAuthError: true }
  );
};
